/* =========================================================
   BALLOON CELEBRATION
   Canvas balloon animation overlay for a Shopify section.

   Based on "Balloon Animation In javascript" (19 Aug 2022, Yash Rajput).
   Reworked for theme use:
     • everything is scoped inside one IIFE -> no globals leak
       (only window.BalloonCelebration is exposed)
     • the overlay canvas is pointer-events:none and is REMOVED
       from the DOM when the show ends, so it can never block
       clicks / scroll on the rest of the page
     • the show auto-hides after a configurable duration
       (default 4s) with a fade out
     • balloon quantity is reduced + configurable, and popping a
       balloon decreases the quantity on screen (no auto respawn
       unless "respawn" is selected)
     • multiple sections / repeat plays are supported
========================================================= */

(function () {
  "use strict";

  if (window.BalloonCelebration) return;

  var FRAME = 1000 / 60;

  var DEFAULTS = {
    countDesktop: 18,
    countMobile: 10,
    sizeMin: 24,
    sizeMax: 60,
    speed: 5,
    durationMs: 4000,
    fadeMs: 600,
    delayMs: 0,
    popBehavior: "remove", // "remove" | "respawn"
    colors: [
      "#FFD700",
      "#DAA520",
      "#D4AF37",
      "#E5C158",
      "#C5A059"
    ],
    stringColor: "#D4AF37",
    showText: false,
    text: "HAPPY BIRTHDAY",
    interactive: true,
    zIndex: 9998
  };

  /* ---------------------------------------------------------
     Helpers
  --------------------------------------------------------- */

  var Tool = {
    randomNumber: function (min, max) {
      return Math.floor(Math.random() * (max - min + 1) + min);
    },
    randomColorHSL: function (hue, saturation, lightness) {
      return "hsl(" + hue + ", " + saturation + "%, " + lightness + "%)";
    },
    pick: function (arr) {
      return arr[Tool.randomNumber(0, arr.length - 1)];
    }
  };

  function Angle(angle) {
    this.a = angle;
    this.rad = (this.a * Math.PI) / 180;
  }
  Angle.prototype.incDec = function (num) {
    this.a += num;
    this.rad = (this.a * Math.PI) / 180;
    return this.rad;
  };

  /* ---------------------------------------------------------
     Balloon
  --------------------------------------------------------- */

  function Shape(scene, x, y, color) {
    this.scene = scene;
    this.ctx = scene.ctx;
    this.init(x, y, color);
  }

  Shape.prototype.init = function (x, y, color) {
    var scene = this.scene;
    this.x = x;
    this.y = y;
    this.r = Tool.randomNumber(scene.sizeMin, scene.sizeMax);
    this.c = color || Tool.pick(scene.colors);
    this.cL = scene.stringColor;
    this.ga = 0.85;
    this.dead = false;
    this.v = { x: 0, y: 0 };
    this.a = new Angle(Tool.randomNumber(0, 360));
    // slight per-balloon speed variation so the group never moves as one block
    this.speed = (scene.speed * Tool.randomNumber(85, 115)) / 100;
  };

  Shape.prototype.draw = function () {
    var ctx = this.ctx;
    var r = this.r;

    ctx.save();
    ctx.globalAlpha = this.ga;
    ctx.fillStyle = this.c;

    // Balloon body + knot
    ctx.beginPath();
    ctx.moveTo(this.x + r / 20, this.y + r);
    ctx.bezierCurveTo(this.x + r, this.y + r / 3, this.x + r, this.y - r, this.x, this.y - r);
    ctx.bezierCurveTo(this.x - r, this.y - r, this.x - r, this.y + r / 3, this.x - r / 20, this.y + r);
    ctx.lineTo(this.x - r / 20, this.y + r + r / 20);
    ctx.lineTo(this.x - r / 20 - r / 20, this.y + r + r / 20);
    ctx.quadraticCurveTo(
      this.x - r / 20 - r / 10,
      this.y + r + r / 20 + r / 20,
      this.x - r / 20 - r / 20,
      this.y + r + r / 20 + r / 20 + r / 20
    );
    ctx.lineTo(this.x + r / 20 + r / 20, this.y + r + r / 20 + r / 20 + r / 20);
    ctx.quadraticCurveTo(
      this.x + r / 20 + r / 10,
      this.y + r + r / 20 + r / 20,
      this.x + r / 20 + r / 20,
      this.y + r + r / 20
    );
    ctx.lineTo(this.x + r / 20, this.y + r + r / 20);
    ctx.closePath();
    ctx.fill();

    // Pointer / touch pop
    if (
      this.scene.interactive &&
      !this.scene.stopping &&
      this.scene.pointerX !== null &&
      ctx.isPointInPath(this.scene.pointerX, this.scene.pointerY)
    ) {
      this.pop();
    }

    // Knot patch + string
    ctx.fillStyle = this.cL;
    ctx.fillRect(this.x - r / 20, this.y + r, r / 10, r / 20);
    ctx.strokeStyle = this.cL;
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(this.x, this.y + r + r / 20 + r / 20 + r / 20);
    ctx.lineTo(this.x, this.y + r + r / 20 + r);
    ctx.stroke();

    // Gloss
    ctx.fillStyle = "white";
    ctx.beginPath();
    ctx.ellipse(
      this.x - r / 2.5,
      this.y - r / 1.5,
      r / 10,
      r / 5,
      (45 * Math.PI) / 180,
      0,
      Math.PI * 2,
      false
    );
    ctx.fill();
    ctx.restore();
  };

  Shape.prototype.pop = function () {
    var scene = this.scene;
    scene.burst(this.x, this.y, this.c);

    if (scene.popBehavior === "respawn") {
      this.init(Tool.randomNumber(0, scene.width), scene.spawnY(this.r), null);
    } else {
      // Quantity on screen goes DOWN as balloons are popped.
      this.dead = true;
    }
  };

  Shape.prototype.updatePosition = function () {
    this.v.x = 0.5 * Math.cos(this.a.rad);
    this.v.y = 0.2 * Math.sin(this.a.rad) - this.speed;
    this.x += this.v.x;
    this.y += this.v.y;
  };

  Shape.prototype.wrapPosition = function () {
    if (this.y + this.r * 2 < 0) {
      if (this.scene.stopping) {
        this.dead = true;
        return;
      }
      this.y = this.scene.spawnY(this.r);
      this.x = Tool.randomNumber(0, this.scene.width);
    }
  };

  Shape.prototype.render = function () {
    this.draw();
    this.updatePosition();
    this.a.incDec(1);
    this.wrapPosition();
  };

  /* ---------------------------------------------------------
     Particle
     Two modes:
       • text mode  -> flies to a letter pixel of the heading
       • burst mode -> confetti puff that fades out and dies
  --------------------------------------------------------- */

  function Particle(scene, x, y, color) {
    this.scene = scene;
    this.ctx = scene.ctx;
    this.init(x, y, color);
  }

  Particle.prototype.init = function (x, y, color) {
    var scene = this.scene;
    var target = scene.textPosArr.length ? scene.textPosArr[scene.particles.length] : null;

    this.textMode = !!target;
    this.xi = target ? target[0] : 0;
    this.yi = target ? target[1] : 0;
    this.x = x;
    this.y = y;
    this.r = Tool.randomNumber(2, 4);
    this.c = color && Math.random() < 0.4 ? color : Tool.pick(scene.colors);
    this.alpha = 1;
    this.dead = false;
    this.random = Math.random() * Math.random();
    this.a = new Angle(Tool.randomNumber(0, 360));
    this.ac = new Angle(0);

    if (this.textMode) {
      this.v = { x: 0, y: 0 };
    } else {
      var dir = new Angle(Tool.randomNumber(0, 360));
      var power = Tool.randomNumber(2, 7);
      this.v = { x: Math.cos(dir.rad) * power, y: Math.sin(dir.rad) * power };
      this.decay = Tool.randomNumber(12, 24) / 1000;
    }
  };

  Particle.prototype.draw = function () {
    var ctx = this.ctx;
    ctx.save();
    ctx.globalAlpha = this.alpha;
    ctx.fillStyle = this.c;
    ctx.beginPath();
    ctx.arc(this.x, this.y, this.r, 0, Math.PI * 2, false);
    ctx.fill();
    ctx.restore();
  };

  Particle.prototype.updatePosition = function () {
    var scene = this.scene;

    if (this.textMode) {
      this.v.x = this.xi - this.x;
      this.v.y = this.yi - this.y;
      if (scene.particles.length !== scene.textPosArr.length) {
        this.x = Math.sin(this.a.rad) * 10 + this.x;
        this.y = Math.cos(this.a.rad) * 10 + this.y;
      } else {
        this.c = Tool.randomColorHSL(Math.sin(this.ac.rad) * 360, 80, 60);
      }
      this.x += this.v.x * this.random;
      this.y += this.v.y * this.random;
      return;
    }

    this.v.y += 0.12; // gravity
    this.v.x *= 0.985;
    this.x += this.v.x;
    this.y += this.v.y;
    this.alpha -= this.decay;
    if (this.alpha <= 0) this.dead = true;
  };

  Particle.prototype.render = function () {
    this.draw();
    this.updatePosition();
    this.a.incDec(1);
    this.ac.incDec(0.1);
  };

  /* ---------------------------------------------------------
     Scene  (one running show)
  --------------------------------------------------------- */

  function Scene(config) {
    var cfg = config || {};

    this.colors = cfg.colors && cfg.colors.length ? cfg.colors : DEFAULTS.colors;
    this.stringColor = cfg.stringColor || DEFAULTS.stringColor;
    this.sizeMin = Math.max(8, cfg.sizeMin || DEFAULTS.sizeMin);
    this.sizeMax = Math.max(this.sizeMin + 4, cfg.sizeMax || DEFAULTS.sizeMax);
    this.speed = cfg.speed || DEFAULTS.speed;
    this.durationMs = cfg.durationMs || DEFAULTS.durationMs;
    this.fadeMs = cfg.fadeMs == null ? DEFAULTS.fadeMs : cfg.fadeMs;
    this.popBehavior = cfg.popBehavior === "respawn" ? "respawn" : "remove";
    this.showText = !!cfg.showText;
    this.text = cfg.text || DEFAULTS.text;
    this.interactive = cfg.interactive !== false;
    this.zIndex = cfg.zIndex || DEFAULTS.zIndex;
    this.countDesktop = cfg.countDesktop || DEFAULTS.countDesktop;
    this.countMobile = cfg.countMobile || DEFAULTS.countMobile;

    this.shapes = [];
    this.particles = [];
    this.textPosArr = [];
    this.pointerX = null;
    this.pointerY = null;
    this.stopping = false;
    this.destroyed = false;
    this.rafId = null;
    this.timers = [];
    this.onEnd = typeof cfg.onEnd === "function" ? cfg.onEnd : null;

    this.build();
  }

  Scene.prototype.build = function () {
    var canvas = document.createElement("canvas");
    canvas.className = "balloon-celebration__canvas";
    canvas.setAttribute("aria-hidden", "true");
    canvas.style.cssText =
      "position:fixed;top:0;left:0;display:block;pointer-events:none;" +
      "opacity:0;transition:opacity " +
      Math.max(120, this.fadeMs) +
      "ms linear;z-index:" +
      this.zIndex +
      ";";

    this.canvas = canvas;
    this.ctx = canvas.getContext("2d");
    this.measure();
    document.body.appendChild(canvas);

    // fade in on next frame so the transition applies
    var self = this;
    requestAnimationFrame(function () {
      if (!self.destroyed) canvas.style.opacity = "1";
    });

    this.spawnShapes();
    if (this.showText) this.buildTextTargets();
    this.bind();
    this.loop();

    // The whole show clears itself off the screen after `durationMs`.
    this.timers.push(
      setTimeout(function () {
        self.stop();
      }, this.durationMs)
    );
  };

  Scene.prototype.measure = function () {
    this.width = this.canvas.width = window.innerWidth;
    this.height = this.canvas.height = window.innerHeight;
    this.isMobile = this.width < 768;
    this.count = this.isMobile ? this.countMobile : this.countDesktop;
    // Balloons must be able to cross the screen inside `durationMs`,
    // otherwise a short show would never reveal the ones spawned lowest.
    var frames = Math.max(30, this.durationMs / FRAME);
    this.travel = this.speed * frames;
  };

  Scene.prototype.spawnY = function (r) {
    // Keep the spawn band inside the distance a balloon can actually travel.
    var band = Math.min(this.height * 0.9, Math.max(this.height * 0.2, this.travel * 0.8));
    return Tool.randomNumber(this.height + r, this.height + r + band);
  };

  Scene.prototype.spawnShapes = function () {
    for (var i = 0; i < this.count; i++) {
      var r = this.sizeMax;
      this.shapes.push(
        new Shape(this, Tool.randomNumber(0, this.width), this.spawnY(r), Tool.pick(this.colors))
      );
    }
  };

  Scene.prototype.burst = function (x, y, color) {
    var max = this.textPosArr.length ? this.textPosArr.length : 600;
    var num = Tool.randomNumber(6, this.isMobile ? 14 : 22);
    for (var i = 0; i < num; i++) {
      if (this.particles.length >= max) break;
      this.particles.push(new Particle(this, x, y, color));
    }
  };

  // Off-screen pass that turns the heading into particle targets.
  Scene.prototype.buildTextTargets = function () {
    var off = document.createElement("canvas");
    off.width = this.width;
    off.height = this.height;
    var ctx = off.getContext("2d");
    var fontSize = this.isMobile ? 200 : 300;

    ctx.fillStyle = "black";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    // Shrink until it fits (loop, not recursion - no stack risk).
    var guard = 0;
    do {
      ctx.font = fontSize + "px sans-serif";
      if (ctx.measureText(this.text).width <= this.width * 0.95) break;
      fontSize -= 2;
      guard++;
    } while (fontSize > 12 && guard < 400);

    ctx.font = fontSize + "px sans-serif";
    ctx.fillText(this.text, this.width / 2, this.height / 2);

    var data = ctx.getImageData(0, 0, this.width, this.height).data;
    for (var y = 0; y < this.height; y += 4) {
      for (var x = 0; x < this.width; x += 4) {
        if (data[(x + y * this.width) * 4 + 3] > 0) this.textPosArr.push([x, y]);
      }
    }
  };

  Scene.prototype.bind = function () {
    var self = this;

    // Listeners live on window, not the canvas: the canvas is
    // pointer-events:none so the page underneath stays clickable.
    this.onPointerMove = function (e) {
      self.pointerX = e.clientX;
      self.pointerY = e.clientY;
    };
    this.onTouchMove = function (e) {
      var touch = e.targetTouches && e.targetTouches[0];
      if (!touch) return;
      self.pointerX = touch.clientX;
      self.pointerY = touch.clientY;
    };
    this.onTouchEnd = function () {
      self.pointerX = null;
      self.pointerY = null;
    };
    this.onResize = function () {
      if (self.destroyed) return;
      self.measure();
    };

    window.addEventListener("mousemove", this.onPointerMove, { passive: true });
    window.addEventListener("touchmove", this.onTouchMove, { passive: true });
    window.addEventListener("touchend", this.onTouchEnd, { passive: true });
    window.addEventListener("resize", this.onResize, { passive: true });
  };

  Scene.prototype.unbind = function () {
    window.removeEventListener("mousemove", this.onPointerMove);
    window.removeEventListener("touchmove", this.onTouchMove);
    window.removeEventListener("touchend", this.onTouchEnd);
    window.removeEventListener("resize", this.onResize);
  };

  Scene.prototype.render = function () {
    this.ctx.clearRect(0, 0, this.width, this.height);

    var i;
    for (i = 0; i < this.shapes.length; i++) this.shapes[i].render();
    for (i = 0; i < this.particles.length; i++) this.particles[i].render();

    // Popped / off-screen items are dropped, so the balloon count
    // on screen only ever goes down during a show.
    this.shapes = this.shapes.filter(function (s) {
      return !s.dead;
    });
    this.particles = this.particles.filter(function (p) {
      return !p.dead;
    });
  };

  Scene.prototype.loop = function () {
    var self = this;
    this.rafId = requestAnimationFrame(function () {
      if (self.destroyed) return;
      self.render();
      self.loop();
    });
  };

  // Fade the overlay out, then take it out of the DOM completely.
  Scene.prototype.stop = function () {
    if (this.stopping || this.destroyed) return;
    this.stopping = true;
    this.canvas.style.opacity = "0";

    var self = this;
    this.timers.push(
      setTimeout(function () {
        self.destroy();
      }, Math.max(120, this.fadeMs))
    );
  };

  Scene.prototype.destroy = function () {
    if (this.destroyed) return;
    this.destroyed = true;

    if (this.rafId) cancelAnimationFrame(this.rafId);
    this.timers.forEach(clearTimeout);
    this.timers = [];
    this.unbind();

    if (this.canvas && this.canvas.parentNode) this.canvas.parentNode.removeChild(this.canvas);

    this.shapes = [];
    this.particles = [];
    this.textPosArr = [];

    if (this.onEnd) this.onEnd();
  };

  /* ---------------------------------------------------------
     Section controller
  --------------------------------------------------------- */

  var running = [];

  function play(config) {
    var cfg = config || {};

    if (cfg.respectReducedMotion !== false && window.matchMedia) {
      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return null;
    }

    var scene = new Scene(cfg);
    running.push(scene);
    var original = scene.onEnd;
    scene.onEnd = function () {
      running = running.filter(function (s) {
        return s !== scene;
      });
      if (original) original();
    };
    return scene;
  }

  function stopAll() {
    running.slice().forEach(function (scene) {
      scene.destroy();
    });
    running = [];
  }

  function parseColors(value) {
    if (!value) return null;
    var list = String(value)
      .split(/[\n;]|,(?![^(]*\))/)
      .map(function (c) {
        return c.trim();
      })
      .filter(Boolean);
    return list.length ? list : null;
  }

  function readConfig(root) {
    var cfg = {};
    try {
      cfg = JSON.parse(root.getAttribute("data-config") || "{}");
    } catch (e) {
      cfg = {};
    }
    cfg.colors = parseColors(cfg.colors);
    return cfg;
  }

  /* ---------------------------------------------------------
     Frequency store  (browser session aware)

       always   -> every page view
       session  -> once per browser session  (sessionStorage:
                   survives reloads + in-tab navigation, clears
                   when the tab/browser is closed)
       interval -> once every X hours       (localStorage)
       once     -> once per visitor, ever   (localStorage)

     Storage is wrapped in try/catch: Safari private mode and
     blocked-cookie setups throw on access, and in that case we
     fall back to an in-memory flag so the show still works for
     the current page view instead of erroring out.
  --------------------------------------------------------- */

  var STORE_PREFIX = "balloon-celebration:";
  var memoryStore = {};

  function storeFor(frequency) {
    try {
      var store = frequency === "session" ? window.sessionStorage : window.localStorage;
      var probe = STORE_PREFIX + "probe";
      store.setItem(probe, "1");
      store.removeItem(probe);
      return store;
    } catch (e) {
      return null;
    }
  }

  function readStamp(frequency, key) {
    var store = storeFor(frequency);
    try {
      return store ? store.getItem(key) : memoryStore[key] || null;
    } catch (e) {
      return memoryStore[key] || null;
    }
  }

  function writeStamp(frequency, key, value) {
    var store = storeFor(frequency);
    memoryStore[key] = value;
    try {
      if (store) store.setItem(key, value);
    } catch (e) {
      /* memoryStore already holds it */
    }
  }

  function hasPlayed(frequency, key, repeatHours) {
    if (frequency === "always") return false;

    var stamp = readStamp(frequency, key);
    if (!stamp) return false;

    if (frequency === "interval") {
      var then = parseInt(stamp, 10);
      if (isNaN(then)) return true;
      var window_ms = Math.max(1, repeatHours || 24) * 3600000;
      return Date.now() - then < window_ms;
    }

    return true;
  }

  function markPlayed(frequency, key) {
    if (frequency === "always") return;
    writeStamp(frequency, key, String(Date.now()));
  }

  // Clears every stored flag - handy for QA: BalloonCelebration.reset()
  function reset() {
    memoryStore = {};
    ["session", "once"].forEach(function (frequency) {
      var store = storeFor(frequency);
      if (!store) return;
      try {
        var keys = [];
        for (var i = 0; i < store.length; i++) {
          var k = store.key(i);
          if (k && k.indexOf(STORE_PREFIX) === 0) keys.push(k);
        }
        keys.forEach(function (k) {
          store.removeItem(k);
        });
      } catch (e) {
        /* nothing to clear */
      }
    });
  }

  // ?balloon=play  -> force it once, ignoring the stored flag
  // ?balloon=reset -> clear the flags, then play
  function urlOverride() {
    try {
      if (!window.location || !window.URLSearchParams) return null;
      var value = new URLSearchParams(window.location.search).get("balloon");
      if (!value) return null;
      value = value.toLowerCase();
      if (value === "reset") {
        reset();
        return "play";
      }
      if (value === "play" || value === "1" || value === "true") return "play";
      if (value === "off" || value === "0" || value === "false") return "off";
      return null;
    } catch (e) {
      return null;
    }
  }

  /* ---------------------------------------------------------
     Section init
  --------------------------------------------------------- */

  function init(root) {
    if (!root || root.dataset.balloonReady === "true") return;
    root.dataset.balloonReady = "true";

    var cfg = readConfig(root);
    var frequency = cfg.frequency || (cfg.oncePerSession ? "session" : "always");
    var key = STORE_PREFIX + (cfg.storageKey || root.dataset.sectionId || "default");
    var inEditor = !!(window.Shopify && window.Shopify.designMode);
    var override = urlOverride();

    // In the theme editor and with ?balloon=play the merchant always
    // sees the animation, otherwise previewing would be impossible.
    var ignoreGate = inEditor || override === "play";

    root.balloonFrequency = frequency;
    root.balloonKey = key;

    // force = skip the frequency gate (editor preview / manual API call)
    root.balloonPlay = function (force) {
      if (!force && !ignoreGate && hasPlayed(frequency, key, cfg.repeatHours)) return null;

      var scene = play(cfg);

      // Only burn the session flag once the show has actually started,
      // so a reduced-motion skip does not block the next real visit.
      if (scene && !inEditor) markPlayed(frequency, key);

      root._balloonScene = scene;
      return scene;
    };

    if (override === "off") return;

    function start() {
      if (!ignoreGate && hasPlayed(frequency, key, cfg.repeatHours)) return;
      var delay = Math.max(0, cfg.delayMs || 0);
      root._balloonTimer = setTimeout(function () {
        root.balloonPlay(ignoreGate);
      }, delay);
    }

    if (cfg.trigger === "manual") {
      // Trigger from anywhere with:
      //   document.dispatchEvent(new CustomEvent('balloon:celebrate'))
      // Pass { detail: { force: true } } to ignore the frequency gate.
      document.addEventListener("balloon:celebrate", function (e) {
        var force = !!(e && e.detail && e.detail.force);
        root.balloonPlay(force || ignoreGate);
      });
      return;
    }

    if (cfg.trigger === "view" && "IntersectionObserver" in window) {
      var observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            observer.disconnect();
            start();
          });
        },
        { threshold: 0, rootMargin: "0px 0px -10% 0px" }
      );
      observer.observe(root);
      return;
    }

    start();
  }

  function initAll(scope) {
    (scope || document).querySelectorAll("[data-balloon-celebration]").forEach(init);
  }

  window.BalloonCelebration = {
    play: play,
    stopAll: stopAll,
    init: init,
    initAll: initAll,
    reset: reset,
    hasPlayed: hasPlayed
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      initAll();
    });
  } else {
    initAll();
  }

  /* Theme editor support */
  document.addEventListener("shopify:section:load", function (e) {
    stopAll();
    initAll(e.target);
  });

  document.addEventListener("shopify:section:unload", function () {
    stopAll();
  });

  document.addEventListener("shopify:section:select", function (e) {
    var root = e.target.querySelector("[data-balloon-celebration]");
    if (root && root.balloonPlay) {
      stopAll();
      root.balloonPlay(true);
    }
  });
})();
